import type { MantineColorsTuple } from "@mantine/core";
import chroma from "chroma-js";

export { hexColorWithOpacity, interpolateColor } from "./utils";

const FOCUS_BASE_COLORS = [
  "#122A09",
  "#2A1F2F",
  "#0B5C41",
  "#141933",
  "#6D0075",
  "#9F0003",
  "#006467",
  "#004F9F",
] as const;

function adjustColor(color: string, brightness: number, saturation: number): string {
  return chroma(color)
    .brighten(brightness / 100)
    .saturate(saturation / 100)
    .hex();
}

export function generateColorTuple(baseColor: string): MantineColorsTuple {
  const brightnessAdjust = 375;
  const saturationAdjust = -15;

  const tint = adjustColor(baseColor, brightnessAdjust, saturationAdjust);
  const highlight = adjustColor(baseColor, brightnessAdjust * 1.0625, saturationAdjust);

  return [
    chroma(tint).brighten(1.5).hex(),
    chroma(tint).brighten(0.75).hex(),
    chroma(tint).brighten(0.3).hex(),
    tint,
    highlight,
    baseColor,
    chroma(baseColor).darken(0.5).hex(),
    chroma(baseColor).darken(1).hex(),
    chroma(baseColor).darken(1.5).hex(),
    chroma(baseColor).darken(2).hex(),
  ];
}

export { generateStandardPalette } from "./palette";
export const graphite = generateColorTuple("#111111");
export {
  GOLD_BASE_HEX,
  primary,
  secondary,
  accent,
  ocean,
  gold,
  legacyBrandOcean,
  legacyBrandGold,
} from "./brand";
export {
  OCEAN_HEX,
  GRAY_HEX,
  oceanRamp,
  grayRamp,
  goldRamp,
  coralRamp,
  pinkRamp,
  lavenderRamp,
  mintRamp,
  oceanMantineTuple,
  grayMantineTuple,
  goldMantineTuple,
  BLACK_HEX,
  INK_HEX,
  INK_OVERLAY_HEX,
  INK_OVERLAY_ALPHA_80,
  GOLD_ANCHOR_500,
  GOLD_ANCHOR_100,
  type ColorStep,
} from "./primitives";

export function generateFocusColors() {
  const focusColors: Record<string, MantineColorsTuple> = {};

  FOCUS_BASE_COLORS.forEach((color, idx) => {
    focusColors[`focus${idx}`] = generateColorTuple(color);
  });

  return focusColors;
}

export function focusColorAtIndex(idx: number, suffix?: "tint" | "highlight" | "", returnHex?: boolean) {
  const focusColors = generateFocusColors();
  const colorKey = `focus${idx % FOCUS_BASE_COLORS.length}`;

  let shade = 5;
  if (suffix === "tint") shade = 3;
  if (suffix === "highlight") shade = 4;

  if (returnHex) {
    return focusColors[colorKey][shade];
  }

  return `${colorKey}.${shade}`;
}

export { contrastTextOnBlend } from "./utils";
